import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease;
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 24px;
  width: 580px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  padding: 0;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5),
              0 0 120px rgba(16, 185, 129, 0.06);
  animation: ${slideUp} 0.4s ease;
`;

export const ProgressBar = styled.div`
  height: 3px;
  background: ${({ theme }) => theme.contrast2 || 'rgba(255,255,255,0.06)'};
  border-radius: 24px 24px 0 0;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #06d6a0);
  width: ${({ $percent }) => $percent}%;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const CardBody = styled.div`
  padding: 44px 44px 36px;
`;

export const Title = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  margin: 0 0 8px 0;
  letter-spacing: -0.3px;
`;

export const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 8px;
`;

export const FeatureCard = styled.div`
  padding: 20px 18px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.contrast || 'rgba(255,255,255,0.02)'};
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.primary}40;
    background: ${({ theme }) => theme.primary}08;
  }
`;

export const FeatureIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: ${({ $color }) => $color || '#10b981'}15;
  border: 1px solid ${({ $color }) => $color || '#10b981'}25;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: ${({ $color }) => $color || '#10b981'};
`;

export const FeatureTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin-bottom: 4px;
`;

export const FeatureDesc = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  line-height: 1.4;
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 32px;
`;

export const PrimaryButton = styled.button`
  padding: 13px 32px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; box-shadow: none; }
`;

export const SecondaryButton = styled.button`
  padding: 13px 24px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  background: transparent;
  color: ${({ theme }) => theme.text};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  opacity: 0.6;

  &:hover { opacity: 1; }
`;

export const TrialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.primary}10;
  border: 1px solid ${({ theme }) => theme.primary}20;
  color: ${({ theme }) => theme.primary};
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 20px;
`;

export const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 8px;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.primary}40;
    background: ${({ theme }) => theme.primary}06;
  }
`;

export const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 12px;
  background: ${({ $done, theme }) => $done ? theme.primary + '15' : theme.contrast2 || 'rgba(255,255,255,0.06)'};
  color: ${({ $done, theme }) => $done ? theme.primary : theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
`;

export const SuccessIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary}20, ${({ theme }) => theme.primary}08);
  border: 2px solid ${({ theme }) => theme.primary}30;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 2rem;
  color: ${({ theme }) => theme.primary};
`;
